import { combineEpics, ofType } from "redux-observable";
import {
    debounceTime,
    delay,
    map,
    mergeMap,
    switchMap,
    withLatestFrom,
} from "rxjs/operators";
import { EMPTY, from, of } from "rxjs";
import {
    setCasingDriftID,
    setCasingID,
    setCasingWeightList,
    setLinerID,
    setLinerOD,
    setLinerODList,
    setLinerWeight,
    setLinerWeightList,
    setTubing1TubingID,
    setTubing1TubingWeight,
    setTubing2TubingID,
    setTubing2TubingWeight,
    setTubing1WeightList,
    setTubing2WeightList,
} from "../../../store/jetlift/jetlift-input/TubingSlice";
import { RootState } from "../../../store/ConfigureStore";
import {
    getCasingDriftID,
    getCasingID,
    getCasingWeightList,
    getLinerID,
    getLinerODList,
    getLinerWeightList,
    getTubingID,
    getTubingWeightList,
} from "../../../services/jetlift/jetlift-input/tubing.service";

const fetchCasingWeightListEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setCasingOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (!tubingInput?.casingOD || !unit?.unitCasingWeightListVal) {
                return [];
            }
            return from(
                getCasingWeightList(
                    +unit?.unitCasingWeightListVal,
                    +tubingInput?.casingOD
                )
            ).pipe(
                mergeMap((data) => {
                    const records = data?.CasingWeightList;
                    if (!records) return [];
                    return [
                        setCasingWeightList(records),
                        setLinerOD(""),
                        setLinerID(""),
                        setLinerWeight(""),
                    ]
                })
            );
        })
    );
};

const fetchLinerODList = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setCasingOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput))
        ),
        switchMap(([_, tubingInput]) => {
            if (!tubingInput?.casingOD) {
                return [];
            }
            return from(getLinerODList(tubingInput?.casingOD ?? 0)).pipe(
                mergeMap((data) => {
                    const records = data?.LinerODList;
                    if (!records) return [];
                    return [setLinerODList(records)];
                })
            );
        })
    );
};

const fetchCasingIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setCasingWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (!tubingInput?.casingOD || !tubingInput?.casingWeight || !unit?.unitCasingIDListVal || !unit?.unitCasingWeightListVal) {
                return [];
            }
            return from(
                getCasingID(
                    +unit?.unitCasingIDListVal,
                    +unit?.unitCasingWeightListVal,
                    tubingInput?.casingOD ?? 0,
                    tubingInput?.casingWeight ?? 0
                )
            ).pipe(
                mergeMap((data) => {
                    const casingID = (data as any)?.CasingID ?? data;
                    return [setCasingID(casingID)];
                })
            );
        })
    );
};

const fetchCasingDriftIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setCasingWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (!tubingInput?.casingOD || !tubingInput?.casingWeight || !unit?.unitCasingIDListVal || !unit?.unitCasingWeightListVal) {
                return [];
            }
            return from(
                getCasingDriftID(
                    +unit?.unitCasingIDListVal,
                    +unit?.unitCasingWeightListVal,
                    tubingInput?.casingOD ?? 0,
                    tubingInput?.casingWeight ?? 0
                )
            ).pipe(
                mergeMap((data) => {
                    const casingDriftID = (data as any)?.CasingDriftID ?? data;
                    return [setCasingDriftID(casingDriftID)];
                })
            );
        })
    );
};

const fetchLinerWeightListEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setLinerOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (!tubingInput?.linerOD || !unit?.unitLinerWeightListVal) {
                return [];
            }
            return from(
                getLinerWeightList(
                    +unit?.unitLinerWeightListVal,
                    tubingInput?.linerOD ?? 0
                )
            ).pipe(
                mergeMap((data) => {
                    const records = data?.LinerWeightList;
                    if (!records) return [];
                    return [
                        setLinerWeightList(records),
                        setLinerID(""),
                        setLinerWeight(""),
                    ];
                })
            );
        })
    );
};

const fetchLinerIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setLinerWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (!tubingInput?.linerOD || !tubingInput?.linerWeight || !unit?.unitLinerIDListVal || !unit?.unitLinerWeightListVal) {
                return [];
            }
            return from(
                getLinerID(
                    +unit?.unitLinerIDListVal,
                    +unit?.unitLinerWeightListVal,
                    tubingInput?.linerOD ?? 0,
                    tubingInput?.linerWeight ?? 0
                )
            ).pipe(mergeMap((data) => [setLinerID(data)]));
        })
    );
};

const fetchTubing1TubingODEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setTubing1TubingOD"),
        debounceTime(1000),
        switchMap(() => {
            return of(setTubing1TubingWeight(""));
        })
    );
};

const fetchTubing1TubingWeightListEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setTubing1TubingTypeUpset", "jetlift/tubing/setTubing1TubingOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (!tubingInput?.tubing1_tubingOD || !tubingInput?.tubing1_tubingTypeUpset) {
                return [];
            }
            return from(
                getTubingWeightList(
                    Number(unit?.unitTubingWeightListVal) ?? 0,
                    tubingInput?.tubing1_tubingTypeUpset,
                    tubingInput?.tubing1_tubingOD ?? 0
                )
            ).pipe(
                mergeMap((data) => {
                    const records = data?.TubingWeightList;
                    if (!records) return [];
                    return [setTubing1WeightList(records)];
                })
            );
        })
    );
};

const fetchTubing1TubingIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setTubing1TubingWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (tubingInput?.tubing1_tubingWeight !== "") {
                return from(
                    getTubingID(
                        +unit?.unitTubingIDListVal,
                        +unit?.unitTubingWeightListVal,
                        tubingInput?.tubing1_tubingOD,
                        tubingInput?.tubing1_tubingWeight
                    )
                ).pipe(mergeMap((data) => {
                    return [setTubing1TubingID(data)]
                }));
            }
            return EMPTY;
        })
    );
};

const fetchTubing2TubingODEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setTubing2TubingOD"),
        debounceTime(1000),
        switchMap(() => {
            return of(setTubing2TubingWeight(""));
        })
    );
};

const fetchTubing2TubingWeightListEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setTubing2TubingTypeUpset", "jetlift/tubing/setTubing2TubingOD"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (!tubingInput?.tubing2_tubingOD || !tubingInput?.tubing2_tubingTypeUpset) {
                return [];
            }
            return from(
                getTubingWeightList(
                    Number(unit?.unitTubingWeightListVal) ?? 0,
                    tubingInput?.tubing2_tubingTypeUpset,
                    tubingInput?.tubing2_tubingOD ?? 0
                )
            ).pipe(
                mergeMap((data) => {
                    const records = data?.TubingWeightList;
                    if (!records) return [];
                    return [setTubing2WeightList(records)];
                })
            );
        })
    );
};

const fetchTubing2TubingIDEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/tubing/setTubing2TubingWeight"),
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([_, tubingInput, unit]) => {
            if (tubingInput?.tubing2_tubingWeight !== "") {
                return from(
                    getTubingID(
                        +unit?.unitTubingIDListVal,
                        +unit?.unitTubingWeightListVal,
                        tubingInput?.tubing2_tubingOD,
                        tubingInput?.tubing2_tubingWeight
                    )
                ).pipe(mergeMap((data) => {
                    return [setTubing2TubingID(data)]
                }));
            }
            return EMPTY;
        })
    );
};


export const jetLiftTubingEpic = combineEpics(
    fetchLinerODList,
    fetchLinerIDEpic,
    fetchCasingIDEpic,
    fetchTubing1TubingODEpic,
    fetchTubing1TubingIDEpic,
    fetchCasingDriftIDEpic,
    fetchLinerWeightListEpic,
    fetchTubing1TubingWeightListEpic,
    fetchCasingWeightListEpic,
    fetchTubing2TubingODEpic,
    fetchTubing2TubingWeightListEpic,
    fetchTubing2TubingIDEpic,
);
